import { copyFileSync, existsSync, mkdirSync, readFileSync, rmSync, writeFileSync } from 'fs';
import { homedir } from 'os';
import { dirname, join } from 'path';
import { randomUUID } from 'crypto';
import { OkraRuntimeError } from '../errors.js';
import { localDocumentRecordSchema, type LocalDocumentRecord } from './types.js';
import { Data, Effect, Schema } from 'effect';

export class LocalStoreError extends Data.TaggedError('LocalStoreError')<{
  readonly method: string;
  readonly status?: number;
  readonly cause: unknown;
}> {}

function localStoreBoundary<A>(method: string, run: () => A): Effect.Effect<A, LocalStoreError> {
  return Effect.try({
    try: run,
    catch: (cause) =>
      new LocalStoreError({
        method,
        ...(cause instanceof OkraRuntimeError ? { status: cause.status } : {}),
        cause,
      }),
  });
}

function runLocalStoreEffect<A>(program: Effect.Effect<A, LocalStoreError>): A {
  return Effect.runSync(program.pipe(Effect.catch((error) => Effect.fail(error.cause))));
}

export const DEFAULT_LOCAL_DATA_DIR = join(homedir(), '.openclaw', 'okra-docs');

export function resolveLocalDataDir(dataDir?: string): string {
  return dataDir || process.env.OKRA_LOCAL_DATA_DIR || DEFAULT_LOCAL_DATA_DIR;
}

export function ensureLocalStoreEffect(dataDir?: string): Effect.Effect<string, LocalStoreError> {
  const resolved = resolveLocalDataDir(dataDir);
  return localStoreBoundary('ensureLocalStore', () => {
    mkdirSync(join(resolved, 'docs'), { recursive: true });
    return resolved;
  });
}

export function ensureLocalStore(dataDir?: string): string {
  return runLocalStoreEffect(ensureLocalStoreEffect(dataDir));
}

export function createLocalDocumentIdEffect(): Effect.Effect<string, LocalStoreError> {
  return localStoreBoundary('createLocalDocumentId', () => `doc-local-${randomUUID()}`);
}

export function createLocalDocumentId(): string {
  return runLocalStoreEffect(createLocalDocumentIdEffect());
}

export function getDocumentDir(documentId: string, dataDir?: string): string {
  return join(resolveLocalDataDir(dataDir), 'docs', documentId);
}

export function getDocumentMetaPath(documentId: string, dataDir?: string): string {
  return join(getDocumentDir(documentId, dataDir), 'meta.json');
}

export function getDocumentPagesDir(documentId: string, dataDir?: string): string {
  return join(getDocumentDir(documentId, dataDir), 'pages');
}

export function getDocumentPagePath(
  documentId: string,
  pageNumber: number,
  dataDir?: string,
): string {
  return join(
    getDocumentPagesDir(documentId, dataDir),
    `page-${String(pageNumber).padStart(4, '0')}.txt`,
  );
}

export function initializeDocumentDirEffect(
  documentId: string,
  dataDir?: string,
): Effect.Effect<string, LocalStoreError> {
  const dir = getDocumentDir(documentId, dataDir);
  return localStoreBoundary('initializeDocumentDir', () => {
    rmSync(dir, { force: true, recursive: true });
    mkdirSync(join(dir, 'pages'), { recursive: true });
    return dir;
  });
}

export function initializeDocumentDir(documentId: string, dataDir?: string): string {
  return runLocalStoreEffect(initializeDocumentDirEffect(documentId, dataDir));
}

export function copySourceIntoStoreEffect(
  documentId: string,
  sourcePath: string,
  dataDir?: string,
): Effect.Effect<string, LocalStoreError> {
  const target = join(getDocumentDir(documentId, dataDir), 'source.pdf');
  return localStoreBoundary('copySourceIntoStore', () => {
    mkdirSync(dirname(target), { recursive: true });
    copyFileSync(sourcePath, target);
    return target;
  });
}

export function copySourceIntoStore(
  documentId: string,
  sourcePath: string,
  dataDir?: string,
): string {
  return runLocalStoreEffect(copySourceIntoStoreEffect(documentId, sourcePath, dataDir));
}

export function writeDocumentRecordEffect(
  record: LocalDocumentRecord,
  dataDir?: string,
): Effect.Effect<void, LocalStoreError> {
  const metaPath = getDocumentMetaPath(record.documentId, dataDir);
  return localStoreBoundary('writeDocumentRecord', () => {
    mkdirSync(dirname(metaPath), { recursive: true });
    writeFileSync(metaPath, JSON.stringify(record, null, 2), 'utf8');
  });
}

export function writeDocumentRecord(record: LocalDocumentRecord, dataDir?: string): void {
  return runLocalStoreEffect(writeDocumentRecordEffect(record, dataDir));
}

export function readDocumentRecordEffect(
  documentId: string,
  dataDir?: string,
): Effect.Effect<LocalDocumentRecord, LocalStoreError> {
  const metaPath = getDocumentMetaPath(documentId, dataDir);
  return localStoreBoundary<unknown>('readDocumentRecord', () => {
    if (!existsSync(metaPath)) {
      throw new OkraRuntimeError(
        'DOCUMENT_NOT_FOUND',
        `Local document not found: ${documentId}`,
        404,
      );
    }
    return JSON.parse(readFileSync(metaPath, 'utf8'));
  }).pipe(
    Effect.flatMap((value) =>
      Schema.decodeUnknownEffect(localDocumentRecordSchema)(value).pipe(
        Effect.mapError(
          (cause) =>
            new LocalStoreError({
              method: 'readDocumentRecord',
              cause,
            }),
        ),
      ),
    ),
    Effect.map((record) => record as LocalDocumentRecord),
  );
}

export function readDocumentRecord(documentId: string, dataDir?: string): LocalDocumentRecord {
  return runLocalStoreEffect(readDocumentRecordEffect(documentId, dataDir));
}

export function writeDocumentPageTextEffect(
  documentId: string,
  pageNumber: number,
  text: string,
  dataDir?: string,
): Effect.Effect<string, LocalStoreError> {
  const pagePath = getDocumentPagePath(documentId, pageNumber, dataDir);
  return localStoreBoundary('writeDocumentPageText', () => {
    mkdirSync(dirname(pagePath), { recursive: true });
    writeFileSync(pagePath, text, 'utf8');
    return pagePath;
  });
}

export function writeDocumentPageText(
  documentId: string,
  pageNumber: number,
  text: string,
  dataDir?: string,
): string {
  return runLocalStoreEffect(writeDocumentPageTextEffect(documentId, pageNumber, text, dataDir));
}

export function readDocumentPageTextEffect(
  documentId: string,
  pageNumber: number,
  dataDir?: string,
): Effect.Effect<string, LocalStoreError> {
  const pagePath = getDocumentPagePath(documentId, pageNumber, dataDir);
  return localStoreBoundary('readDocumentPageText', () => {
    if (!existsSync(pagePath)) {
      throw new OkraRuntimeError(
        'DOCUMENT_NOT_FOUND',
        `Local page ${pageNumber} not found for document ${documentId}`,
        404,
      );
    }
    return readFileSync(pagePath, 'utf8');
  });
}

export function readDocumentPageText(
  documentId: string,
  pageNumber: number,
  dataDir?: string,
): string {
  return runLocalStoreEffect(readDocumentPageTextEffect(documentId, pageNumber, dataDir));
}
